import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Package, Plus, Trash2, Edit, DollarSign, TrendingUp, ShoppingCart, Users, AlertCircle, Check, X, Search, PackageCheck, PackageMinus, PackageX } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);

const emptyProduct = { name: "", unit: "unidade", quantity: 0, cost_price: 0, sale_price: 0, sale_price_wholesale: 0, min_stock: 0, sku: "", notes: "" };
const PAYMENT_LABELS = { avista: "À vista", aprazo: "A prazo", pix: "Pix", credito: "Crédito", dinheiro: "Dinheiro" };
const emptySupplier = { name: "", cnpj: "", cpf: "", address: "", phone: "", email: "", notes: "" };
const emptyEntry = { supplier_id: "", financial_mode: "conta_pagar", due_date: "", bank_account_id: "", notes: "", items: [{ product_id: "", quantity: "", unit_cost: "" }] };
const emptySale = { items: [{ product_id: "", quantity: "", unit_price: "" }], client_id: "", client_name_manual: "", payment_method: "avista", price_type: "varejo", discount: 0, notes: "", bank_account_id: "" };
const emptyLoss = { product_id: "", quantity: "", reason: "" };

export default function DistributorModule({ companyId, clients, permissions = [], isAdmin = false, bankAccounts = [] }) {
  const can = (code) => isAdmin || permissions.includes(code);
  const canManageProducts = can("manage_distributor_products");
  const canCreateEntries = can("create_distributor_entries");
  const canCreateSales = can("create_distributor_sales");
  const canManageSuppliers = can("manage_distributor_suppliers");
  const canViewReports = can("view_distributor_reports");
  const [tab, setTab] = useState("dashboard");
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [movements, setMovements] = useState([]);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");

  // Dialogs
  const [supDlg, setSupDlg] = useState({ open: false, data: { ...emptySupplier } });
  const [prodDlg, setProdDlg] = useState({ open: false, data: { ...emptyProduct } });
  const [entryDlg, setEntryDlg] = useState({ open: false, data: { ...emptyEntry } });
  const [saleDlg, setSaleDlg] = useState({ open: false, data: { ...emptySale } });
  const [lossDlg, setLossDlg] = useState({ open: false, data: { ...emptyLoss } });

  const fetchAll = async () => {
    if (!companyId) return;
    try {
      const [s, p, sa, m, r] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/distributor/suppliers`),
        axios.get(`${API}/companies/${companyId}/distributor/products`),
        axios.get(`${API}/companies/${companyId}/distributor/sales`),
        axios.get(`${API}/companies/${companyId}/distributor/movements`),
        axios.get(`${API}/companies/${companyId}/distributor/report/profitability`),
      ]);
      setSuppliers(s.data || []);
      setProducts(p.data || []);
      setSales(sa.data || []);
      setMovements(m.data || []);
      setReport(r.data || null);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar Distribuidora");
    }
  };
  useEffect(() => { fetchAll(); }, [companyId]);

  // ===== Supplier =====
  const saveSupplier = async () => {
    if (!supDlg.data.name?.trim()) return toast.error("Nome obrigatório");
    try {
      if (supDlg.data.id) {
        await axios.put(`${API}/companies/${companyId}/distributor/suppliers/${supDlg.data.id}`, supDlg.data);
      } else {
        await axios.post(`${API}/companies/${companyId}/distributor/suppliers`, supDlg.data);
      }
      toast.success("Fornecedor salvo");
      setSupDlg({ open: false, data: { ...emptySupplier } });
      fetchAll();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };
  const delSupplier = async (id) => {
    if (!window.confirm("Excluir fornecedor?")) return;
    await axios.delete(`${API}/companies/${companyId}/distributor/suppliers/${id}`); toast.success("Excluído"); fetchAll();
  };

  // ===== Product =====
  const saveProduct = async () => {
    if (!prodDlg.data.name?.trim()) return toast.error("Nome obrigatório");
    const payload = {
      ...prodDlg.data,
      quantity: parseFloat(prodDlg.data.quantity) || 0,
      cost_price: parseFloat(prodDlg.data.cost_price) || 0,
      sale_price: parseFloat(prodDlg.data.sale_price) || 0,
      sale_price_wholesale: parseFloat(prodDlg.data.sale_price_wholesale) || 0,
      min_stock: parseFloat(prodDlg.data.min_stock) || 0,
    };
    try {
      if (prodDlg.data.id) {
        await axios.put(`${API}/companies/${companyId}/distributor/products/${prodDlg.data.id}`, payload);
      } else {
        await axios.post(`${API}/companies/${companyId}/distributor/products`, payload);
      }
      toast.success("Produto salvo");
      setProdDlg({ open: false, data: { ...emptyProduct } });
      fetchAll();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    await axios.delete(`${API}/companies/${companyId}/distributor/products/${id}`); toast.success("Excluído"); fetchAll();
  };

  // ===== Perda / Ajuste =====
  const submitLoss = async () => {
    const d = lossDlg.data;
    if (!d.product_id) return toast.error("Produto obrigatório");
    const qty = parseFloat(d.quantity) || 0;
    if (qty <= 0) return toast.error("Informe a quantidade perdida");
    if (!d.reason?.trim()) return toast.error("Informe a justificativa");
    try {
      const res = await axios.post(`${API}/companies/${companyId}/distributor/adjust`, {
        product_id: d.product_id,
        quantity_delta: -Math.abs(qty), // sempre negativo para perda
        reason: d.reason.trim(),
      });
      toast.success(`Perda registrada · Novo estoque: ${res.data.new_quantity}`);
      setLossDlg({ open: false, data: { ...emptyLoss } });
      fetchAll();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  // ===== Entrada =====
  const entryTotals = useMemo(() => {
    const items = entryDlg.data.items || [];
    return items.reduce((sum, it) => sum + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0)), 0);
  }, [entryDlg.data.items]);

  const submitEntry = async () => {
    const d = entryDlg.data;
    const items = (d.items || []).filter(it => it.product_id && parseFloat(it.quantity) > 0 && parseFloat(it.unit_cost) > 0);
    if (items.length === 0) return toast.error("Adicione ao menos 1 item completo");
    if (d.financial_mode === "conta_pagar" && !d.due_date) return toast.error("Selecione a data de vencimento");
    if (d.financial_mode === "saida_direta" && !d.bank_account_id) return toast.error("Selecione a conta bancária de origem para saída direta");
    try {
      const res = await axios.post(`${API}/companies/${companyId}/distributor/entries-bulk`, {
        items: items.map(it => ({ product_id: it.product_id, quantity: parseFloat(it.quantity), unit_cost: parseFloat(it.unit_cost) })),
        supplier_id: d.supplier_id || null,
        financial_mode: d.financial_mode,
        due_date: d.due_date || null,
        bank_account_id: d.financial_mode === "saida_direta" ? d.bank_account_id : null,
        notes: d.notes || null,
      });
      toast.success(res.data?.message || "Entrada registrada");
      setEntryDlg({ open: false, data: { ...emptyEntry, items: [{ product_id: "", quantity: "", unit_cost: "" }] } });
      fetchAll();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  // ===== Venda =====
  const saleTotals = useMemo(() => {
    const d = saleDlg.data;
    let sub = 0, cost = 0;
    (d.items || []).forEach(it => {
      const q = parseFloat(it.quantity) || 0;
      const p = parseFloat(it.unit_price) || 0;
      sub += q * p;
      const prod = products.find(x => x.id === it.product_id);
      cost += q * (parseFloat(prod?.cost_price) || 0);
    });
    const total = Math.max(sub - (parseFloat(d.discount) || 0), 0);
    return { subtotal: sub, cost, total, profit: total - cost };
  }, [saleDlg.data, products]);

  const submitSale = async () => {
    const d = saleDlg.data;
    const items = (d.items || []).filter(it => it.product_id && parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) > 0);
    if (items.length === 0) return toast.error("Adicione ao menos 1 item completo");
    if (d.payment_method !== "aprazo" && !d.bank_account_id) {
      return toast.error("Selecione a conta bancária que receberá o valor");
    }
    try {
      const res = await axios.post(`${API}/companies/${companyId}/distributor/sales`, {
        items: items.map(it => ({ product_id: it.product_id, quantity: parseFloat(it.quantity), unit_price: parseFloat(it.unit_price) })),
        client_id: d.client_id || null,
        client_name_manual: d.client_name_manual || null,
        payment_method: d.payment_method,
        price_type: d.price_type || "varejo",
        discount: parseFloat(d.discount) || 0,
        notes: d.notes || null,
        bank_account_id: d.payment_method === "aprazo" ? null : (d.bank_account_id || null),
      });
      toast.success(`Venda #${res.data.sale_number} registrada · Lucro: ${fmt(res.data.profit)}`);
      setSaleDlg({ open: false, data: { ...emptySale } });
      fetchAll();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  const productsBelowMin = products.filter(p => (p.quantity || 0) <= (p.min_stock || 0) && (p.min_stock || 0) > 0);
  const totalStockValue = products.reduce((s, p) => s + ((p.quantity || 0) * (p.cost_price || 0)), 0);
  const totalSaleValue = products.reduce((s, p) => s + ((p.quantity || 0) * (p.sale_price || 0)), 0);
  const totalSaleValueWholesale = products.reduce((s, p) => s + ((p.quantity || 0) * (p.sale_price_wholesale || 0)), 0);

  const filteredProducts = products.filter(p => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="distributor-module">
      <style>{`
        [data-testid="distributor-module"] input,
        [data-testid="distributor-module"] textarea,
        [data-testid="distributor-module"] [role="combobox"],
        [data-testid="distributor-module"] th,
        [data-testid="distributor-module"] td,
        [data-testid="distributor-module"] label,
        [data-testid="distributor-module"] p,
        [data-testid="distributor-module"] span,
        [data-testid="distributor-module"] h1,
        [data-testid="distributor-module"] h2,
        [data-testid="distributor-module"] h3,
        [data-testid="distributor-module"] h4 { color: #000 !important; }
        [data-testid="distributor-module"] input::placeholder,
        [data-testid="distributor-module"] textarea::placeholder { color: #6b7280 !important; }
      `}</style>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl md:text-2xl font-bold text-black flex items-center gap-2">
          <Package className="h-6 w-6 text-blue-600" /> Distribuidora
        </h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="inline-flex w-full overflow-x-auto whitespace-nowrap h-auto">
          <TabsTrigger value="dashboard" data-testid="dist-tab-dashboard" className="text-black data-[state=active]:text-blue-700">Dashboard</TabsTrigger>
          <TabsTrigger value="produtos" data-testid="dist-tab-produtos" className="text-black data-[state=active]:text-blue-700">Produtos</TabsTrigger>
          {canCreateEntries && <TabsTrigger value="entradas" data-testid="dist-tab-entradas" className="text-black data-[state=active]:text-blue-700">Entradas</TabsTrigger>}
          {canCreateSales && <TabsTrigger value="vendas" data-testid="dist-tab-vendas" className="text-black data-[state=active]:text-blue-700">Vendas</TabsTrigger>}
          {canManageSuppliers && <TabsTrigger value="fornecedores" data-testid="dist-tab-fornecedores" className="text-black data-[state=active]:text-blue-700">Fornecedores</TabsTrigger>}
          {canViewReports && <TabsTrigger value="relatorio" data-testid="dist-tab-relatorio" className="text-black data-[state=active]:text-blue-700">Lucratividade</TabsTrigger>}
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Card className="bg-blue-50">
              <CardContent className="p-4">
                <p className="text-xs text-black">Total Produtos</p>
                <p className="text-xl md:text-2xl font-bold text-blue-700">{products.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4">
                <p className="text-xs text-black">Valor em Estoque (custo)</p>
                <p className="text-xl md:text-2xl font-bold text-green-700">{fmt(totalStockValue)}</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="p-4">
                <p className="text-xs text-black">Valor Potencial (venda)</p>
                <div className="flex flex-col gap-0.5 mt-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-black">Varejo</span>
                    <span className="text-base md:text-lg font-bold text-purple-700" data-testid="dist-potential-retail">{fmt(totalSaleValue)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-black">Atacado</span>
                    <span className="text-base md:text-lg font-bold text-indigo-700" data-testid="dist-potential-wholesale">{fmt(totalSaleValueWholesale)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="p-4">
                <p className="text-xs text-black">Produtos com Estoque Baixo</p>
                <p className="text-xl md:text-2xl font-bold text-red-700">{productsBelowMin.length}</p>
              </CardContent>
            </Card>
          </div>
          {productsBelowMin.length > 0 && (
            <Card className="border-red-200">
              <CardHeader><CardTitle className="text-base text-red-700 flex items-center gap-2 text-black"><AlertCircle className="h-5 w-5" /> Alerta de Estoque Baixo</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {productsBelowMin.map(p => (
                    <div key={p.id} className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <div>
                        <p className="font-medium text-black">{p.name}</p>
                        <p className="text-xs text-black">Mín: {p.min_stock} {p.unit} · Atual: <strong>{p.quantity}</strong></p>
                      </div>
                      <Button size="sm" onClick={() => { setEntryDlg({ open: true, data: { ...emptyEntry, items: [{ product_id: p.id, quantity: "", unit_cost: p.cost_price }] } }); }} disabled={!canCreateEntries} className={!canCreateEntries ? "opacity-40" : ""}>
                        <Plus className="h-3 w-3 mr-1" /> Comprar
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Produtos */}
        <TabsContent value="produtos" className="space-y-3">
          <div className="flex justify-between gap-2 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input type="text" placeholder="Buscar produto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="dist-product-search" />
            </div>
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={() => setProdDlg({ open: true, data: { ...emptyProduct } })} data-testid="dist-new-product" disabled={!canManageProducts}>
              <Plus className="h-4 w-4 mr-1" /> Novo Produto
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 text-black">SKU</th>
                  <th className="text-left p-2 text-black">Produto</th>
                  <th className="text-center p-2 text-black">Unid.</th>
                  <th className="text-right p-2 text-black">Estoque</th>
                  <th className="text-right p-2 text-black">Mín.</th>
                  <th className="text-right p-2 text-black">Custo</th>
                  <th className="text-right p-2 text-black">Varejo</th>
                  <th className="text-right p-2 text-black">Atacado</th>
                  <th className="text-right p-2 text-black">Margem</th>
                  <th className="text-center p-2 text-black">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(p => {
                  const margin = p.cost_price > 0 ? ((p.sale_price - p.cost_price) / p.cost_price * 100) : 0;
                  const low = (p.quantity || 0) <= (p.min_stock || 0) && (p.min_stock || 0) > 0;
                  return (
                    <tr key={p.id} className={`border-t ${low ? "bg-red-50" : ""}`}>
                      <td className="p-2 text-black text-xs">{p.sku || "—"}</td>
                      <td className="p-2 text-black font-medium">{p.name}</td>
                      <td className="p-2 text-center text-black">{p.unit}</td>
                      <td className={`p-2 text-right font-bold ${low ? "text-red-600" : "text-black"}`}>{p.quantity}</td>
                      <td className="p-2 text-right text-black">{p.min_stock}</td>
                      <td className="p-2 text-right text-black">{fmt(p.cost_price)}</td>
                      <td className="p-2 text-right text-black">{fmt(p.sale_price)}</td>
                      <td className="p-2 text-right text-black">{fmt(p.sale_price_wholesale)}</td>
                      <td className="p-2 text-right"><Badge variant={margin > 20 ? "default" : "outline"} className="text-xs">{margin.toFixed(1)}%</Badge></td>
                      <td className="p-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" onClick={() => setProdDlg({ open: true, data: { ...p } })} disabled={!canManageProducts}><Edit className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" className="text-orange-600" title="Registrar Perda / Avaria" onClick={() => setLossDlg({ open: true, data: { ...emptyLoss, product_id: p.id } })} disabled={!canManageProducts} data-testid={`dist-loss-btn-${p.id}`}><PackageX className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => delProduct(p.id)} disabled={!canManageProducts}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredProducts.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-black">Nenhum produto cadastrado</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Entradas */}
        <TabsContent value="entradas" className="space-y-3">
          <div className="flex justify-end">
            <Button className="bg-green-500 hover:bg-green-600" onClick={() => setEntryDlg({ open: true, data: { ...emptyEntry } })} data-testid="dist-new-entry" disabled={!canCreateEntries}>
              <PackageCheck className="h-4 w-4 mr-1" /> Nova Entrada
            </Button>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base text-black">Histórico de Movimentações</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2 text-black">Data</th>
                      <th className="text-left p-2 text-black">Tipo</th>
                      <th className="text-left p-2 text-black">Produto</th>
                      <th className="text-right p-2 text-black">Qtd</th>
                      <th className="text-right p-2 text-black">Valor Un.</th>
                      <th className="text-right p-2 text-black">Total</th>
                      <th className="text-left p-2 text-black">Fornecedor/Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.slice(0, 200).map(m => (
                      <tr key={m.id} className="border-t hover:bg-gray-50">
                        <td className="p-2 text-black text-xs">{new Date(m.created_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}</td>
                        <td className="p-2"><Badge variant={m.type === "entrada" ? "default" : m.type === "venda" ? "outline" : "destructive"} className="text-xs">{m.type}</Badge></td>
                        <td className="p-2 text-black">{m.product_name}</td>
                        <td className="p-2 text-right">{m.quantity}</td>
                        <td className="p-2 text-right text-black">{fmt(m.unit_price || m.unit_cost || 0)}</td>
                        <td className="p-2 text-right font-bold">{fmt(m.total || 0)}</td>
                        <td className="p-2 text-black text-xs">{m.supplier_name || m.client_name || m.reason || "—"}</td>
                      </tr>
                    ))}
                    {movements.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-black">Sem movimentações</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas */}
        <TabsContent value="vendas" className="space-y-3">
          <div className="flex justify-end">
            <Button className="bg-purple-500 hover:bg-purple-600" onClick={() => setSaleDlg({ open: true, data: { ...emptySale, items: [{ product_id: "", quantity: "", unit_price: "" }] } })} data-testid="dist-new-sale" disabled={!canCreateSales}>
              <ShoppingCart className="h-4 w-4 mr-1" /> Nova Venda
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 text-black">#</th>
                  <th className="text-left p-2 text-black">Data</th>
                  <th className="text-left p-2 text-black">Cliente</th>
                  <th className="text-right p-2 text-black">Itens</th>
                  <th className="text-right p-2 text-black">Total</th>
                  <th className="text-right p-2 text-black">Saldo</th>
                  <th className="text-right p-2 text-black">Lucro</th>
                  <th className="text-center p-2 text-black">Pgto.</th>
                </tr>
              </thead>
              <tbody>
                {sales.slice(0, 200).map(s => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 text-black font-mono">#{String(s.sale_number).padStart(4, "0")}</td>
                    <td className="p-2 text-black text-xs">{new Date(s.created_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}</td>
                    <td className="p-2 text-black">{s.client_name || "Consumidor"}</td>
                    <td className="p-2 text-right">{(s.items || []).length}</td>
                    <td className="p-2 text-right font-bold text-black">{fmt(s.total)}</td>
                    <td className="p-2 text-right font-bold" data-testid={`dist-sale-balance-${s.id}`}>
                      <span className={s.payment_status === "pago" ? "text-green-600" : (s.payment_status === "parcial" ? "text-amber-600" : "text-red-600")}>
                        {fmt(s.balance_due || 0)}
                      </span>
                    </td>
                    <td className="p-2 text-right font-bold text-green-600">{fmt(s.profit)}</td>
                    <td className="p-2 text-center"><Badge variant="outline" className="text-xs">{PAYMENT_LABELS[s.payment_method] || s.payment_method}</Badge></td>
                  </tr>
                ))}
                {sales.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-black">Nenhuma venda</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Fornecedores */}
        <TabsContent value="fornecedores" className="space-y-3">
          <div className="flex justify-end">
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={() => setSupDlg({ open: true, data: { ...emptySupplier } })} data-testid="dist-new-supplier" disabled={!canManageSuppliers}>
              <Plus className="h-4 w-4 mr-1" /> Novo Fornecedor
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 text-black">Nome</th>
                  <th className="text-left p-2 text-black">CNPJ/CPF</th>
                  <th className="text-left p-2 text-black">Endereço</th>
                  <th className="text-left p-2 text-black">Contato</th>
                  <th className="text-center p-2 text-black">Ações</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 text-black font-medium">{s.name}</td>
                    <td className="p-2 text-black">{s.cnpj || s.cpf || "—"}</td>
                    <td className="p-2 text-black text-xs">{s.address || "—"}</td>
                    <td className="p-2 text-black text-xs">{s.phone || s.email || "—"}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" onClick={() => setSupDlg({ open: true, data: { ...s } })} disabled={!canManageSuppliers}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => delSupplier(s.id)} disabled={!canManageSuppliers}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-black">Nenhum fornecedor</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Relatório */}
        <TabsContent value="relatorio" className="space-y-3">
          {report ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><p className="text-xs text-black">Vendas</p><p className="text-xl md:text-2xl font-bold text-blue-700">{report.total_sales_count}</p></CardContent></Card>
                <Card className="bg-amber-50" data-testid="dist-report-receivable"><CardContent className="p-4"><p className="text-xs text-black">Saldo a Receber</p><p className="text-xl md:text-2xl font-bold text-amber-700">{fmt(report.total_receivable || 0)}</p><p className="text-[11px] text-black">Recebido: {fmt(report.total_paid || 0)} / Total: {fmt(report.total_sales_amount)}</p></CardContent></Card>
                <Card className="bg-red-50"><CardContent className="p-4"><p className="text-xs text-black">Custo Total</p><p className="text-xl md:text-2xl font-bold text-red-700">{fmt(report.total_cost)}</p></CardContent></Card>
                <Card className="bg-purple-50"><CardContent className="p-4"><p className="text-xs text-black">Lucro · Margem</p><p className="text-xl md:text-2xl font-bold text-purple-700">{fmt(report.total_profit)}</p><p className="text-xs text-black">{report.margin_percent.toFixed(1)}%</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-base text-black">Top 20 Produtos por Lucro</CardTitle></CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead className="bg-gray-100"><tr><th className="text-left p-2">Produto</th><th className="text-right p-2">Qtd Vendida</th><th className="text-right p-2">Faturamento</th><th className="text-right p-2">Lucro</th></tr></thead>
                      <tbody>
                        {(report.top_products || []).map(p => (
                          <tr key={p.product_id} className="border-t"><td className="p-2 text-black">{p.product_name}</td><td className="p-2 text-right">{p.quantity_sold}</td><td className="p-2 text-right">{fmt(p.sales_total)}</td><td className="p-2 text-right font-bold text-green-600">{fmt(p.profit)}</td></tr>
                        ))}
                        {(report.top_products || []).length === 0 && <tr><td colSpan={4} className="p-4 text-center text-black">Sem vendas</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : <p className="text-black text-center py-4">Carregando...</p>}
        </TabsContent>
      </Tabs>

      {/* Dialog Fornecedor */}
      <Dialog open={supDlg.open} onOpenChange={(open) => setSupDlg({ ...supDlg, open })}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto distributor-dialog text-black">
          <DialogHeader><DialogTitle className="text-black">{supDlg.data.id ? "Editar" : "Novo"} Fornecedor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-bold text-black">Nome *</label><Input value={supDlg.data.name} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, name: e.target.value } })} data-testid="dist-sup-name" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><label className="text-xs font-bold text-black">CNPJ</label><Input value={supDlg.data.cnpj} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, cnpj: e.target.value } })} /></div>
              <div><label className="text-xs font-bold text-black">CPF</label><Input value={supDlg.data.cpf} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, cpf: e.target.value } })} /></div>
            </div>
            <div><label className="text-xs font-bold text-black">Endereço</label><Input value={supDlg.data.address} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, address: e.target.value } })} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><label className="text-xs font-bold text-black">Telefone</label><Input value={supDlg.data.phone} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, phone: e.target.value } })} /></div>
              <div><label className="text-xs font-bold text-black">Email</label><Input type="email" value={supDlg.data.email} onChange={(e) => setSupDlg({ ...supDlg, data: { ...supDlg.data, email: e.target.value } })} /></div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSupDlg({ open: false, data: { ...emptySupplier } })}>Cancelar</Button><Button className="bg-blue-600" onClick={saveSupplier}>Salvar</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Produto */}
      <Dialog open={prodDlg.open} onOpenChange={(open) => setProdDlg({ ...prodDlg, open })}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto distributor-dialog text-black">
          <DialogHeader><DialogTitle className="text-black">{prodDlg.data.id ? "Editar" : "Novo"} Produto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="md:col-span-2"><label className="text-xs font-bold text-black">Nome *</label><Input value={prodDlg.data.name} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, name: e.target.value } })} data-testid="dist-prod-name" /></div>
              <div><label className="text-xs font-bold text-black">SKU</label><Input value={prodDlg.data.sku} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, sku: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><label className="text-xs font-bold text-black">Unidade</label><Input value={prodDlg.data.unit} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, unit: e.target.value } })} placeholder="un, kg, cx..." /></div>
              <div><label className="text-xs font-bold text-black">Estoque Inicial</label><Input type="number" step="0.01" value={prodDlg.data.quantity} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, quantity: e.target.value } })} /></div>
              <div><label className="text-xs font-bold text-black">Estoque Mín.</label><Input type="number" step="0.01" value={prodDlg.data.min_stock} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, min_stock: e.target.value } })} /></div>
              <div><label className="text-xs font-bold text-black">Custo (R$)</label><Input type="number" step="0.01" value={prodDlg.data.cost_price} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, cost_price: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><label className="text-xs font-bold text-black">Preço Varejo (R$) *</label><Input type="number" step="0.01" value={prodDlg.data.sale_price} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, sale_price: e.target.value } })} data-testid="dist-prod-price-retail" /></div>
              <div><label className="text-xs font-bold text-black">Preço Atacado (R$)</label><Input type="number" step="0.01" value={prodDlg.data.sale_price_wholesale} onChange={(e) => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, sale_price_wholesale: e.target.value } })} data-testid="dist-prod-price-wholesale" /></div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setProdDlg({ open: false, data: { ...emptyProduct } })}>Cancelar</Button><Button className="bg-blue-600" onClick={saveProduct} data-testid="dist-prod-save">Salvar</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Entrada */}
      <Dialog open={entryDlg.open} onOpenChange={(open) => setEntryDlg({ ...entryDlg, open })}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto distributor-dialog text-black">
          <DialogHeader><DialogTitle className="text-black">Nova Entrada de Estoque</DialogTitle><DialogDescription className="text-black">Vários produtos com o mesmo fornecedor · gera uma única conta a pagar</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-bold text-black">Fornecedor</label>
              <Select value={entryDlg.data.supplier_id || "none"} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, supplier_id: v === "none" ? "" : v } })}>
                <SelectTrigger className="text-black" data-testid="dist-entry-supplier"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Sem fornecedor —</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="border rounded-lg p-2 space-y-2">
              <p className="text-xs font-bold text-black">Itens</p>
              {entryDlg.data.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Select value={it.product_id} onValueChange={(v) => {
                      const p = products.find(x => x.id === v);
                      const items = [...entryDlg.data.items];
                      items[idx] = { ...it, product_id: v, unit_cost: it.unit_cost || p?.cost_price || "" };
                      setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items } });
                    }}>
                      <SelectTrigger className="text-black" data-testid={`dist-entry-product-${idx}`}><SelectValue placeholder="Produto" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" step="0.01" placeholder="Qtd" value={it.quantity} onChange={(e) => { const items = [...entryDlg.data.items]; items[idx] = { ...it, quantity: e.target.value }; setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items } }); }} data-testid={`dist-entry-qty-${idx}`} /></div>
                  <div className="col-span-3"><Input type="number" step="0.01" placeholder="Custo Un." value={it.unit_cost} onChange={(e) => { const items = [...entryDlg.data.items]; items[idx] = { ...it, unit_cost: e.target.value }; setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items } }); }} /></div>
                  <div className="col-span-1"><Button size="sm" variant="outline" className="text-red-600" onClick={() => { const items = entryDlg.data.items.filter((_, i) => i !== idx); setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items: items.length ? items : [{ product_id: "", quantity: "", unit_cost: "" }] } }); }}><X className="h-3 w-3" /></Button></div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items: [...entryDlg.data.items, { product_id: "", quantity: "", unit_cost: "" }] } })} data-testid="dist-entry-add-item"><Plus className="h-3 w-3 mr-1" /> Adicionar Item</Button>
            </div>
            <div className="text-sm text-black text-right">Total da Entrada: <strong className="text-black text-lg">{fmt(entryTotals)}</strong></div>
            <div><label className="text-xs font-bold text-black">Impacto Financeiro *</label>
              <Select value={entryDlg.data.financial_mode} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, financial_mode: v } })}>
                <SelectTrigger data-testid="dist-entry-fin"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conta_pagar">Gerar Conta a Pagar (com aprovação)</SelectItem>
                  <SelectItem value="saida_direta">Saída direta no caixa (sem aprovação)</SelectItem>
                  <SelectItem value="sem_financeiro">Só estoque (não afeta caixa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {entryDlg.data.financial_mode === "conta_pagar" && (
              <div><label className="text-xs font-bold text-black">Vencimento *</label><Input type="date" value={entryDlg.data.due_date} onChange={(e) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, due_date: e.target.value } })} /></div>
            )}
            {entryDlg.data.financial_mode === "saida_direta" && (
              <div><label className="text-xs font-bold text-black">Conta Bancária (origem) *</label>
                <Select value={entryDlg.data.bank_account_id || ""} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, bank_account_id: v } })}>
                  <SelectTrigger data-testid="dist-entry-bank"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                  <SelectContent>
                    {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEntryDlg({ open: false, data: { ...emptyEntry, items: [{ product_id: "", quantity: "", unit_cost: "" }] } })}>Cancelar</Button><Button className="bg-green-600" onClick={submitEntry} data-testid="dist-entry-save">Registrar Entrada</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Venda */}
      <Dialog open={saleDlg.open} onOpenChange={(open) => setSaleDlg({ ...saleDlg, open })}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto distributor-dialog text-black">
          <DialogHeader><DialogTitle className="text-black">Nova Venda</DialogTitle><DialogDescription className="text-black">Cria automaticamente entrada no caixa e baixa estoque</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><label className="text-xs font-bold text-black">Cliente</label>
                <Select value={saleDlg.data.client_id || "none"} onValueChange={(v) => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, client_id: v === "none" ? "" : v } })}>
                  <SelectTrigger className="text-black"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Consumidor —</SelectItem>{(clients || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs font-bold text-black">Nome (manual)</label><Input value={saleDlg.data.client_name_manual} onChange={(e) => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, client_name_manual: e.target.value } })} placeholder="Se sem cadastro" /></div>
            </div>
            <div className="border rounded-lg p-2 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-bold text-black">Itens</p>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-black">Tabela de Preço</label>
                  <Select value={saleDlg.data.price_type} onValueChange={(v) => {
                    // Ao mudar tabela, recalcula preço dos itens já selecionados
                    const items = saleDlg.data.items.map(it => {
                      if (!it.product_id) return it;
                      const p = products.find(x => x.id === it.product_id);
                      const newPrice = v === "atacado" ? (p?.sale_price_wholesale || p?.sale_price || 0) : (p?.sale_price || 0);
                      return { ...it, unit_price: newPrice };
                    });
                    setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, price_type: v, items } });
                  }}>
                    <SelectTrigger className="text-black w-32 h-8" data-testid="dist-sale-price-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="varejo">Varejo</SelectItem>
                      <SelectItem value="atacado">Atacado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {saleDlg.data.items.map((it, idx) => {
                const prod = products.find(p => p.id === it.product_id);
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Select value={it.product_id} onValueChange={(v) => {
                        const p = products.find(x => x.id === v);
                        const defaultPrice = saleDlg.data.price_type === "atacado"
                          ? (p?.sale_price_wholesale || p?.sale_price || "")
                          : (p?.sale_price || "");
                        const items = [...saleDlg.data.items];
                        items[idx] = { ...it, product_id: v, unit_price: defaultPrice };
                        setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items } });
                      }}>
                        <SelectTrigger className="text-black"><SelectValue placeholder="Produto" /></SelectTrigger>
                        <SelectContent>{products.filter(p => (p.quantity || 0) > 0).map(p => <SelectItem key={p.id} value={p.id}>{p.name} (estq: {p.quantity})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Input type="number" step="0.01" placeholder="Qtd" value={it.quantity} onChange={(e) => { const items = [...saleDlg.data.items]; items[idx] = { ...it, quantity: e.target.value }; setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items } }); }} /></div>
                    <div className="col-span-3"><Input type="number" step="0.01" placeholder="Preço Un." value={it.unit_price} onChange={(e) => { const items = [...saleDlg.data.items]; items[idx] = { ...it, unit_price: e.target.value }; setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items } }); }} /></div>
                    <div className="col-span-1 text-right text-sm">{prod ? fmt((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)) : "—"}</div>
                    <div className="col-span-1"><Button size="sm" variant="outline" className="text-red-600" onClick={() => { const items = saleDlg.data.items.filter((_, i) => i !== idx); setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items: items.length ? items : [{ product_id: "", quantity: "", unit_price: "" }] } }); }}><X className="h-3 w-3" /></Button></div>
                  </div>
                );
              })}
              <Button size="sm" variant="outline" onClick={() => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items: [...saleDlg.data.items, { product_id: "", quantity: "", unit_price: "" }] } })}><Plus className="h-3 w-3 mr-1" /> Adicionar Item</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div><label className="text-xs font-bold text-black">Desconto (R$)</label><Input type="number" step="0.01" value={saleDlg.data.discount} onChange={(e) => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, discount: e.target.value } })} /></div>
              <div><label className="text-xs font-bold text-black">Pagamento</label>
                <Select value={saleDlg.data.payment_method} onValueChange={(v) => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, payment_method: v } })}>
                  <SelectTrigger className="text-black" data-testid="dist-sale-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avista">À vista</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">Pix</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                    <SelectItem value="aprazo">A prazo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-right"><p className="text-xs text-black">Total</p><p className="text-2xl font-bold text-purple-700">{fmt(saleTotals.total)}</p><p className="text-xs text-green-600">Lucro: {fmt(saleTotals.profit)}</p></div>
            </div>
            {saleDlg.data.payment_method !== "aprazo" && (
              <div>
                <label className="text-xs font-bold text-black">Conta que receberá o valor *</label>
                <Select
                  value={saleDlg.data.bank_account_id || ""}
                  onValueChange={(v) => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, bank_account_id: v } })}
                >
                  <SelectTrigger className="text-black" data-testid="dist-sale-bank"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · {fmt(b.current_balance)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-600 mt-1">O valor entrará como <b>Entrada</b> no saldo da conta selecionada.</p>
              </div>
            )}
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSaleDlg({ open: false, data: { ...emptySale } })}>Cancelar</Button><Button className="bg-purple-600" onClick={submitSale} data-testid="dist-sale-save">Registrar Venda</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Perda / Avaria */}
      <Dialog open={lossDlg.open} onOpenChange={(open) => setLossDlg({ ...lossDlg, open })}>
        <DialogContent className="max-w-md w-[95vw] distributor-dialog text-black">
          <DialogHeader>
            <DialogTitle className="text-black flex items-center gap-2"><PackageX className="h-5 w-5 text-orange-600" /> Registrar Perda / Avaria</DialogTitle>
            <DialogDescription className="text-black">Baixa itens do estoque sem impacto financeiro. Registra a justificativa para auditoria.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(() => {
              const p = products.find(x => x.id === lossDlg.data.product_id);
              if (!p) return null;
              return (
                <div className="p-2 bg-gray-50 rounded border">
                  <p className="text-sm font-bold text-black">{p.name}</p>
                  <p className="text-xs text-black">Estoque atual: <strong>{p.quantity} {p.unit}</strong></p>
                </div>
              );
            })()}
            <div>
              <label className="text-xs font-bold text-black">Quantidade Perdida *</label>
              <Input type="number" step="0.01" min="0.01" value={lossDlg.data.quantity} onChange={(e) => setLossDlg({ ...lossDlg, data: { ...lossDlg.data, quantity: e.target.value } })} placeholder="Ex: 2" data-testid="dist-loss-qty" />
            </div>
            <div>
              <label className="text-xs font-bold text-black">Justificativa *</label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm text-black"
                value={lossDlg.data.reason}
                onChange={(e) => setLossDlg({ ...lossDlg, data: { ...lossDlg.data, reason: e.target.value } })}
                placeholder="Ex: Avaria por umidade, saco furado, vencimento, quebra no transporte..."
                data-testid="dist-loss-reason"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLossDlg({ open: false, data: { ...emptyLoss } })}>Cancelar</Button>
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={submitLoss} data-testid="dist-loss-save">Registrar Perda</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
